import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../components/Notification';
import {
  PlusIcon, TrashIcon, EyeIcon, LogoutIcon, EditIcon, DownloadIcon, CheckCircleIcon
} from '../components/Icons';
import {
  getAllProducts, deleteProduct, getProductUrl,
  type ProductDocument
} from '../firebase';
import { OfflineService, type CacheProgress } from '../services/offlineService';
import { resolveText } from '../types';

export default function ProductsListPage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { showNotification, NotificationComponent } = useNotification();

  const [products, setProducts] = useState<ProductDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [swReady, setSwReady] = useState(false);
  const [downloadingProducts, setDownloadingProducts] = useState<Map<string, CacheProgress>>(new Map());
  const [cachedProducts, setCachedProducts] = useState<Set<string>>(new Set());

  // Service Worker registration
  useEffect(() => {
    async function initServiceWorker() {
      const success = await OfflineService.register();
      if (success) {
        setSwReady(true);
        // Get cached products list
        const cachedIds = OfflineService.getCachedProductIds();
        setCachedProducts(new Set(cachedIds));
      }
    }
    initServiceWorker();
  }, []);

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    setLoading(true);
    try {
      const data = await getAllProducts();
      setProducts(data);
    } catch (error) {
      showNotification('Ürünler yüklenemedi!', 'error');
    }
    setLoading(false);
  }

  const handleDelete = async (id: string, productName: string) => {
    if (confirm(`"${productName}" ürününü silmek istediğinize emin misiniz?`)) {
      try {
        await deleteProduct(id);
        showNotification('Ürün silindi!', 'success');
        loadProducts();
      } catch (error) {
        showNotification('Silme hatası!', 'error');
      }
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleDownload = useCallback(async (product: ProductDocument) => {
    if (!swReady) {
      showNotification('Offline mod hazır değil!', 'error');
      return;
    }

    if (downloadingProducts.has(product.id)) {
      return; // Already downloading
    }

    // Initialize progress
    setDownloadingProducts(prev => {
      const next = new Map(prev);
      next.set(product.id, { productId: product.id, loaded: 0, failed: 0, total: 0 });
      return next;
    });

    await OfflineService.cacheProduct(
      product.id,
      product,
      // onProgress
      (progress) => {
        setDownloadingProducts(prev => {
          const next = new Map(prev);
          next.set(product.id, progress);
          return next;
        });
      },
      // onComplete
      (result) => {
        setDownloadingProducts(prev => {
          const next = new Map(prev);
          next.delete(product.id);
          return next;
        });

        if (result.success) {
          setCachedProducts(prev => new Set([...prev, product.id]));
          showNotification(`"${resolveText(product.name, 'tr')}" offline kullanıma hazır!`, 'success');
        } else {
          showNotification(`İndirme tamamlandı (${result.failed} görsel yüklenemedi)`, 'error');
        }
      },
      // onError
      (error) => {
        setDownloadingProducts(prev => {
          const next = new Map(prev);
          next.delete(product.id);
          return next;
        });
        showNotification(`İndirme hatası: ${error.error}`, 'error');
      }
    );
  }, [swReady, downloadingProducts, showNotification]);

  const handleDeleteCache = useCallback(async (productId: string, productName: string) => {
    if (confirm(`"${productName}" ürününün offline verisini silmek istediğinize emin misiniz?`)) {
      await OfflineService.deleteProductCache(productId);
      setCachedProducts(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
      showNotification('Offline veri silindi!', 'success');
    }
  }, [showNotification]);

  return (
    <div className="config-page">
      {/* Header */}
      <header className="config-header">
        <div className="config-header-inner">
          <Link to="/" className="logo">
            <img src="/logodeneme5-scaled.png" alt="Sibuzhome" className="logo-img" />
          </Link>
          <h1 className="config-title">Ürün Yönetimi</h1>
          <div className="config-header-actions">
            <Link to="/add-product" className="btn-secondary">
              <PlusIcon />
              <span>Yeni Ürün</span>
            </Link>
            <button type="button" className="btn-secondary" onClick={handleLogout}>
              <LogoutIcon />
              <span>Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="products-page-content">
        {loading ? (
          <div className="loading-state">
            <div className="loader-spinner"></div>
            <p>Yükleniyor...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h2>Henüz ürün eklenmemiş</h2>
            <p>İlk ürününüzü ekleyerek başlayın</p>
            <Link to="/add-product" className="btn-secondary">
              <PlusIcon />
              <span>Yeni Ürün Ekle</span>
            </Link>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-list-card">
                <div className="product-list-card-header">
                  <h3 className="product-list-card-name">{resolveText(product.name, 'tr')}</h3>
                  {product.badge && (
                    <span className="product-list-card-badge">{resolveText(product.badge, 'tr')}</span>
                  )}
                </div>
                
                <div className="product-list-card-id">
                  ID: <code>{product.id}</code>
                </div>

                <div className="product-list-card-variants">
                  {(product.colorVariants || []).map(v => (
                    <span
                      key={v.id}
                      className="product-list-card-swatch"
                      style={{ backgroundColor: v.color }}
                      title={resolveText(v.name, 'tr')}
                    />
                  ))}
                  <span className="product-list-card-variant-count">
                    {(product.colorVariants || []).length} renk, {(product.customVariants || []).length} seçenek
                  </span>
                </div>

                <div className="product-list-card-actions">
                  <a
                    href={getProductUrl(product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-action"
                  >
                    <EyeIcon />
                    <span>Görüntüle</span>
                  </a>
                  <Link to={`/edit-product/${product.id}`} className="btn-action">
                    <EditIcon />
                    <span>Düzenle</span>
                  </Link>
                  {swReady && (
                    cachedProducts.has(product.id) ? (
                      <button
                        type="button"
                        className="btn-action btn-action-cached"
                        onClick={() => handleDeleteCache(product.id, resolveText(product.name, 'tr'))}
                        title="Offline veriyi sil"
                      >
                        <CheckCircleIcon />
                        <span>Offline</span>
                      </button>
                    ) : downloadingProducts.has(product.id) ? (
                      <div className="btn-action btn-action-downloading">
                        <div
                          className="download-progress-bar"
                          style={{
                            width: `${downloadingProducts.get(product.id)!.total > 0
                              ? (downloadingProducts.get(product.id)!.loaded / downloadingProducts.get(product.id)!.total) * 100
                              : 0}%`
                          }}
                        />
                        <span className="download-progress-text">
                          {downloadingProducts.get(product.id)!.loaded}/{downloadingProducts.get(product.id)!.total}
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn-action btn-action-download"
                        onClick={() => handleDownload(product)}
                        title="Offline kullanım için indir"
                      >
                        <DownloadIcon />
                        <span>İndir</span>
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="btn-action btn-action-danger"
                    onClick={() => handleDelete(product.id, resolveText(product.name, 'tr'))}
                  >
                    <TrashIcon />
                    <span>Sil</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {NotificationComponent}

      <style>{`
        .products-page-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 24px;
        }

        .loading-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
          color: #666;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 16px;
        }

        .empty-state h2 {
          margin: 0 0 8px 0;
          color: #1a1a1a;
          font-size: 1.5rem;
        }

        .empty-state p {
          margin: 0 0 24px 0;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }

        .product-list-card {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          padding: 24px;
          transition: box-shadow 0.2s, transform 0.2s;
        }

        .product-list-card:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }

        .product-list-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }

        .product-list-card-name {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a1a1a;
        }

        .product-list-card-badge {
          background: #c9a962;
          color: white;
          padding: 4px 10px;
          border-radius: 100px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .product-list-card-id {
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 16px;
        }

        .product-list-card-id code {
          background: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: monospace;
          color: #1a1a1a;
        }

        .product-list-card-variants {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 20px;
        }

        .product-list-card-swatch {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 0 0 1px rgba(0,0,0,0.1);
        }

        .product-list-card-variant-count {
          font-size: 0.8rem;
          color: #888;
          margin-left: 8px;
        }

        .product-list-card-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .btn-action {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 0.85rem;
          color: #1a1a1a;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-action:hover {
          background: #e5e7eb;
        }

        .btn-action svg {
          width: 14px;
          height: 14px;
        }

        .btn-action-danger:hover {
          background: #fef2f2;
          color: #dc2626;
          border-color: #fecaca;
        }

        .btn-action-download {
          background: #eff6ff;
          color: #2563eb;
          border-color: #bfdbfe;
        }

        .btn-action-download:hover {
          background: #dbeafe;
        }

        .btn-action-cached {
          background: #f0fdf4;
          color: #16a34a;
          border-color: #bbf7d0;
        }

        .btn-action-cached:hover {
          background: #dcfce7;
        }

        .btn-action-downloading {
          position: relative;
          overflow: hidden;
          background: #f3f4f6;
          min-width: 80px;
          justify-content: center;
        }

        .download-progress-bar {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          background: #3b82f6;
          opacity: 0.2;
          transition: width 0.2s ease;
        }

        .download-progress-text {
          position: relative;
          z-index: 1;
          font-size: 0.75rem;
          color: #1e40af;
          font-weight: 600;
        }

        .loader-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e8e8e8;
          border-top-color: #e6a532;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          .products-page-content {
            padding: 24px 16px;
          }

          .products-grid {
            grid-template-columns: 1fr;
          }

          .config-header-actions .btn-secondary span {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}
